import { DateTime } from 'luxon';
import type { CopilotMentionSelection, CopilotPanelScope } from './copilot.types';

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize Ask AI panel / gateway `dateRange` to inclusive YYYY-MM-DD.
 *
 * Report URLs and `DateRange.asApiParams()` use exclusive-end ISO instants.
 * The sidepanel date inputs clamp future days for display only — without this,
 * the UI can show Aug 2–4 while the wire still sends an exclusive week end
 * (e.g. Aug 9/10), so Ask AI cites and queries a different window than the picker.
 */
export function normalizeInclusivePanelDateRange(
  from?: string,
  to?: string,
  options?: { timeZone?: string, today?: string },
): { from: string, to: string } | null {
  const rawFrom = from?.trim() ?? '';
  const rawTo = to?.trim() ?? '';
  if (!rawFrom || !rawTo) {
    return null;
  }

  const zone = options?.timeZone?.trim() || undefined;
  const today =
    options?.today?.slice(0, 10) ||
    DateTime.now().setZone(zone || 'utc').toISODate() ||
    '';

  let fromDay = calendarDayFromBound(rawFrom, zone, false);
  let toDay = calendarDayFromBound(rawTo, zone, rawTo.includes('T'));

  if (!ISO_DAY_RE.test(fromDay) || !ISO_DAY_RE.test(toDay)) {
    return null;
  }

  // Date-only `to` after today is treated as exclusive (same as gateway).
  if (!rawTo.includes('T') && today && toDay > today) {
    toDay = addCalendarDays(toDay, -1);
  }

  if (today && toDay > today) {
    toDay = today;
  }
  if (fromDay > toDay) {
    fromDay = toDay;
  }
  return { from: fromDay, to: toDay };
}

/**
 * Calendar YYYY-MM-DD for a bound.
 * Exclusive ISO ends use instant−1ms then the calendar day in zone — slicing the
 * UTC YYYY-MM-DD and subtracting a day is wrong for non-midnight UTC ends
 * (e.g. `2026-08-04T21:00:00Z` = exclusive Aug 5 00:00 +03 → inclusive Aug 4).
 */
function calendarDayFromBound(
  raw: string,
  timeZone: string | undefined,
  exclusiveEnd: boolean,
): string {
  const sliced = raw.slice(0, 10);
  if (!raw.includes('T')) {
    return sliced;
  }
  const dt = DateTime.fromISO(raw, { setZone: true });
  if (!dt.isValid) {
    return sliced;
  }
  const instant = exclusiveEnd ? dt.minus({ milliseconds: 1 }) : dt;
  const zoned = timeZone ? instant.setZone(timeZone) : instant;
  if (!zoned.isValid) {
    return sliced;
  }
  return zoned.toISODate() ?? sliced;
}

function addCalendarDays(day: string, delta: number): string {
  const dt = DateTime.fromISO(day.slice(0, 10), { zone: 'utc' });
  if (!dt.isValid) {
    return day.slice(0, 10);
  }
  return dt.plus({ days: delta }).toISODate() ?? day.slice(0, 10);
}

/** Prefer panel override, report TZ, then @-selected user (single), then viewer/company. */
export function resolvePanelTimezone(
  mentionSelection: CopilotMentionSelection | undefined,
  panelScope: CopilotPanelScope | undefined,
  viewerTimezone: string | undefined,
  companyTimezone?: string | undefined,
  reportTimezone?: string | undefined,
): string | undefined {
  const users = mentionSelection?.selectedUsers ?? [];
  if (users.length >= 2 && reportTimezone?.trim()) {
    return reportTimezone.trim();
  }
  if (panelScope?.timezone?.trim()) {
    return panelScope.timezone.trim();
  }
  if (reportTimezone?.trim()) {
    return reportTimezone.trim();
  }
  if (users.length === 1 && users[0]?.timezone?.trim()) {
    return users[0].timezone.trim();
  }
  const viewer = viewerTimezone?.trim();
  if (viewer) {
    return viewer;
  }
  return companyTimezone?.trim() || undefined;
}

/** Default reporting window when live data is connected and no range is set yet. */
export function defaultLast7DaysRange(timezone: string): { from: string, to: string } {
  const zone = timezone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const end = DateTime.now().setZone(zone).startOf('day');
  const start = end.minus({ days: 6 });
  return {
    from: start.toISODate() ?? '',
    to: end.toISODate() ?? '',
  };
}

export function panelScopeHasDateRange(scope: CopilotPanelScope | undefined): boolean {
  const from = scope?.dateRange?.from?.slice(0, 10);
  const to = scope?.dateRange?.to?.slice(0, 10);
  return Boolean(from && to);
}
